// src/store/slices/testimonialSlice.ts
import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import { testimonialService } from "../../services/api/testimonialService";
import { ITestimonial } from "../../types/testimonial";

interface TestimonialState {
  testimonials: ITestimonial[];
  selectedTestimonial: ITestimonial | null;
  loading: boolean;
  selectedTestimonialLoading: boolean;
  error: string | null;
  selectedTestimonialError: string | null;
  lastFetched: number | null;
}

const initialState: TestimonialState = {
  testimonials: [],
  selectedTestimonial: null,
  loading: false,
  selectedTestimonialLoading: false,
  error: null,
  selectedTestimonialError: null,
  lastFetched: null,
};

export const getAllTestimonials = createAsyncThunk(
  "testimonials/fetchAll",
  async (_, { rejectWithValue, getState }) => {
    try {
      const state = getState() as { testimonials: TestimonialState };
      const now = Date.now();
      const fiveMinutes = 5 * 60 * 1000;

      if (
        state.testimonials.lastFetched &&
        now - state.testimonials.lastFetched < fiveMinutes &&
        state.testimonials.testimonials.length > 0
      ) {
        return state.testimonials.testimonials;
      }

      const testimonials = await testimonialService.getAllTestimonials();
      return testimonials;
    } catch (error: any) {
      return rejectWithValue(
        error.response?.data?.message || "Failed to fetch testimonials"
      );
    }
  }
);

export const fetchTestimonialById = createAsyncThunk(
  "testimonials/fetchById",
  async (testimonialId: string, { rejectWithValue }) => {
    try {
      const testimonial = await testimonialService.getTestimonialById(
        testimonialId
      );
      return testimonial;
    } catch (error: any) {
      return rejectWithValue(
        error.response?.data?.message || "Failed to fetch testimonial"
      );
    }
  }
);

const testimonialSlice = createSlice({
  name: "testimonials",
  initialState,
  reducers: {
    clearSelectedTestimonial: (state) => {
      state.selectedTestimonial = null;
      state.selectedTestimonialError = null;
    },
    clearErrors: (state) => {
      state.error = null;
      state.selectedTestimonialError = null;
    },
    invalidateCache: (state) => {
      state.lastFetched = null;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(getAllTestimonials.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(getAllTestimonials.fulfilled, (state, action) => {
        state.loading = false;
        state.testimonials = action.payload;
        state.lastFetched = Date.now();
      })
      .addCase(getAllTestimonials.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })
      .addCase(fetchTestimonialById.pending, (state) => {
        state.selectedTestimonialLoading = true;
        state.selectedTestimonialError = null;
      })
      .addCase(fetchTestimonialById.fulfilled, (state, action) => {
        state.selectedTestimonialLoading = false;
        state.selectedTestimonial = action.payload;
        const index = state.testimonials.findIndex(
          (d) => d.testimonialId === action.payload.testimonialId
        );
        if (index !== -1) {
          state.testimonials[index] = action.payload;
        }
      })
      .addCase(fetchTestimonialById.rejected, (state, action) => {
        state.selectedTestimonialLoading = false;
        state.selectedTestimonialError = action.payload as string;
      });
  },
});

export const { clearSelectedTestimonial, clearErrors, invalidateCache } =
  testimonialSlice.actions;
export default testimonialSlice.reducer;
